import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, AlertTriangleIcon, CreditCardIcon, CalendarIcon, ClockIcon, FileTextIcon, UploadIcon, CheckCircleIcon, XCircleIcon, DownloadIcon, TrashIcon } from 'lucide-react';
import { Dispute, DisputeStatus, EvidenceDocument } from '../types/Dispute';
export function DisputeDetail() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [showEvidenceForm, setShowEvidenceForm] = useState(false);
  // Mock data - in a real app, this would be fetched from an API
  const dispute: Dispute = {
    id: id || '1',
    disputeId: 'DP-2024-001',
    transactionId: 'TXN-2024-1234',
    amount: 125000,
    currency: 'LKR',
    status: 'evidence_required',
    reason: 'fraudulent',
    reasonDescription: 'Customer claims transaction was unauthorized',
    createdAt: '2024-01-15T10:30:00Z',
    dueDate: '2024-01-29T23:59:59Z',
    customerName: 'John Smith',
    customerEmail: 'john.smith@email.com',
    cardBrand: 'Visa',
    cardLast4: '4242',
    networkReasonCode: '10.4',
    isChargeback: true,
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-15T10:30:00Z',
      status: 'new',
      description: 'Dispute opened by cardholder'
    }, {
      id: '2',
      timestamp: '2024-01-15T11:00:00Z',
      status: 'evidence_required',
      description: 'Evidence submission required'
    }]
  };
  const getStatusBadge = (status: DisputeStatus) => {
    const styles = {
      new: 'bg-blue-100 text-blue-800',
      evidence_required: 'bg-yellow-100 text-yellow-800',
      evidence_submitted: 'bg-purple-100 text-purple-800',
      under_review: 'bg-orange-100 text-orange-800',
      won: 'bg-green-100 text-green-800',
      lost: 'bg-red-100 text-red-800',
      closed: 'bg-gray-100 text-gray-800',
      expired: 'bg-red-100 text-red-800'
    };
    const labels = {
      new: 'New',
      evidence_required: 'Evidence Required',
      evidence_submitted: 'Evidence Submitted',
      under_review: 'Under Review',
      won: 'Won',
      lost: 'Lost',
      closed: 'Closed',
      expired: 'Expired'
    };
    return <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${styles[status]}`} data-id="element-3809">
        {labels[status]}
      </span>;
  };
  const getDaysRemaining = (dueDate: string) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diff = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };
  const daysRemaining = getDaysRemaining(dispute.dueDate);
  const isUrgent = daysRemaining <= 3 && daysRemaining >= 0;
  return <div className="space-y-6" data-id="element-3810">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-3811">
        <button onClick={() => navigate('/disputes')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-3812">
          <ArrowLeftIcon size={20} data-id="element-3813" />
          Back to Disputes
        </button>
      </div>
      {/* Dispute Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-3814">
        <div className="flex items-start justify-between mb-4" data-id="element-3815">
          <div data-id="element-3816">
            <div className="flex items-center gap-3 mb-2" data-id="element-3817">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-3818">
                {dispute.disputeId}
              </h1>
              {dispute.isChargeback && <span className="px-3 py-1 bg-red-100 text-red-800 text-sm font-medium rounded" data-id="element-3819">
                  Chargeback
                </span>}
            </div>
            <div className="text-sm text-gray-500" data-id="element-3820">
              Transaction: {dispute.transactionId}
            </div>
          </div>
          {getStatusBadge(dispute.status)}
        </div>
        {/* Alert for urgent disputes */}
        {isUrgent && <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg" data-id="element-3821">
            <div className="flex items-start" data-id="element-3822">
              <AlertTriangleIcon size={20} className="text-red-600 mt-0.5 mr-3" data-id="element-3823" />
              <div data-id="element-3824">
                <div className="text-sm font-medium text-red-900" data-id="element-3825">
                  Urgent: Response required soon
                </div>
                <div className="text-sm text-red-700 mt-1" data-id="element-3826">
                  You have {daysRemaining} days remaining to submit evidence for
                  this dispute.
                </div>
              </div>
            </div>
          </div>}
        {/* Key Information */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-3827">
          <div data-id="element-3828">
            <div className="text-sm text-gray-500 mb-1" data-id="element-3829">Amount</div>
            <div className="text-2xl font-semibold text-gray-900" data-id="element-3830">
              Rs. {dispute.amount.toLocaleString('en-LK')}
            </div>
          </div>
          <div data-id="element-3831">
            <div className="text-sm text-gray-500 mb-1" data-id="element-3832">Due Date</div>
            <div className="text-lg font-medium text-gray-900" data-id="element-3833">
              {new Date(dispute.dueDate).toLocaleDateString('en-GB')}
            </div>
            <div className={`text-sm ${isUrgent ? 'text-red-600 font-medium' : 'text-gray-500'}`} data-id="element-3834">
              {daysRemaining} days remaining
            </div>
          </div>
          <div data-id="element-3835">
            <div className="text-sm text-gray-500 mb-1" data-id="element-3836">Created</div>
            <div className="text-lg font-medium text-gray-900" data-id="element-3837">
              {new Date(dispute.createdAt).toLocaleDateString('en-GB')}
            </div>
          </div>
        </div>
      </div>
      {/* Customer & Transaction Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3838">
        <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-3839">
          <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-3840">
            Customer Information
          </h3>
          <div className="space-y-3" data-id="element-3841">
            <div data-id="element-3842">
              <div className="text-sm text-gray-500" data-id="element-3843">Name</div>
              <div className="text-base font-medium text-gray-900" data-id="element-3844">
                {dispute.customerName}
              </div>
            </div>
            <div data-id="element-3845">
              <div className="text-sm text-gray-500" data-id="element-3846">Email</div>
              <div className="text-base font-medium text-gray-900" data-id="element-3847">
                {dispute.customerEmail}
              </div>
            </div>
            <div data-id="element-3848">
              <div className="text-sm text-gray-500" data-id="element-3849">Payment Method</div>
              <div className="flex items-center gap-2" data-id="element-3850">
                <CreditCardIcon size={16} className="text-gray-400" data-id="element-3851" />
                <span className="text-base font-medium text-gray-900" data-id="element-3852">
                  {dispute.cardBrand} •••• {dispute.cardLast4}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-3853">
          <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-3854">
            Dispute Reason
          </h3>
          <div className="space-y-3" data-id="element-3855">
            <div data-id="element-3856">
              <div className="text-sm text-gray-500" data-id="element-3857">Category</div>
              <div className="text-base font-medium text-gray-900 capitalize" data-id="element-3858">
                {dispute.reason.replace(/_/g, ' ')}
              </div>
            </div>
            {dispute.networkReasonCode && <div data-id="element-3859">
                <div className="text-sm text-gray-500" data-id="element-3860">Network Reason Code</div>
                <div className="text-base font-medium text-gray-900" data-id="element-3861">
                  {dispute.networkReasonCode}
                </div>
              </div>}
            {dispute.reasonDescription && <div data-id="element-3862">
                <div className="text-sm text-gray-500" data-id="element-3863">Description</div>
                <div className="text-base text-gray-900" data-id="element-3864">
                  {dispute.reasonDescription}
                </div>
              </div>}
          </div>
        </div>
      </div>
      {/* Evidence Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-3865">
        <div className="flex items-center justify-between mb-4" data-id="element-3866">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-3867">
            Evidence & Documentation
          </h3>
          {dispute.status === 'evidence_required' && <button onClick={() => setShowEvidenceForm(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-3868">
              <UploadIcon size={18} data-id="element-3869" />
              Submit Evidence
            </button>}
        </div>
        {dispute.evidence ? <div className="space-y-4" data-id="element-3870">
            {dispute.evidence.customerCommunication && <div data-id="element-3871">
                <div className="text-sm font-medium text-gray-700 mb-1" data-id="element-3872">
                  Customer Communication
                </div>
                <div className="text-sm text-gray-600" data-id="element-3873">
                  {dispute.evidence.customerCommunication}
                </div>
              </div>}
            {dispute.evidence.shippingDocumentation && <div data-id="element-3874">
                <div className="text-sm font-medium text-gray-700 mb-1" data-id="element-3875">
                  Shipping Documentation
                </div>
                <div className="text-sm text-gray-600" data-id="element-3876">
                  {dispute.evidence.shippingDocumentation}
                </div>
              </div>}
            {dispute.evidence.documents.length > 0 && <div data-id="element-3877">
                <div className="text-sm font-medium text-gray-700 mb-2" data-id="element-3878">
                  Uploaded Documents
                </div>
                <div className="space-y-2" data-id="element-3879">
                  {dispute.evidence.documents.map(doc => <div key={doc.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg" data-id="element-3880">
                      <div className="flex items-center gap-3" data-id="element-3881">
                        <FileTextIcon size={20} className="text-gray-400" data-id="element-3882" />
                        <div data-id="element-3883">
                          <div className="text-sm font-medium text-gray-900" data-id="element-3884">
                            {doc.name}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-3885">
                            {(doc.size / 1000).toFixed(0)} KB • Uploaded{' '}
                            {new Date(doc.uploadedAt).toLocaleDateString('en-GB')}
                          </div>
                        </div>
                      </div>
                      <button className="p-2 text-blue-600 hover:bg-blue-50 rounded" data-id="element-3886">
                        <DownloadIcon size={18} data-id="element-3887" />
                      </button>
                    </div>)}
                </div>
              </div>}
            {dispute.evidence.submittedAt && <div className="pt-3 border-t border-gray-200" data-id="element-3888">
                <div className="text-sm text-gray-500" data-id="element-3889">
                  Evidence submitted on{' '}
                  {new Date(dispute.evidence.submittedAt).toLocaleDateString('en-GB')}
                </div>
              </div>}
          </div> : <div className="text-center py-8 text-gray-500" data-id="element-3890">
            No evidence has been submitted yet
          </div>}
      </div>
      {/* Lifecycle Timeline */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-3891">
        <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-3892">
          Dispute Timeline
        </h3>
        <div className="space-y-4" data-id="element-3893">
          {dispute.lifecycle.map((event, index) => <div key={event.id} className="flex gap-4" data-id="element-3894">
              <div className="flex flex-col items-center" data-id="element-3895">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${index === dispute.lifecycle.length - 1 ? 'bg-blue-100' : 'bg-gray-100'}`} data-id="element-3896">
                  {event.status === 'won' ? <CheckCircleIcon size={16} className="text-green-600" data-id="element-3897" /> : event.status === 'lost' ? <XCircleIcon size={16} className="text-red-600" data-id="element-3898" /> : <ClockIcon size={16} className={index === dispute.lifecycle.length - 1 ? 'text-blue-600' : 'text-gray-400'} data-id="element-3899" />}
                </div>
                {index < dispute.lifecycle.length - 1 && <div className="w-0.5 h-12 bg-gray-200 my-1" data-id="element-3900"></div>}
              </div>
              <div className="flex-1 pb-4" data-id="element-3901">
                <div className="text-sm font-medium text-gray-900" data-id="element-3902">
                  {event.description}
                </div>
                <div className="text-xs text-gray-500 mt-1" data-id="element-3903">
                  {new Date(event.timestamp).toLocaleString('en-GB')}
                </div>
                {event.actor && <div className="text-xs text-gray-500 mt-1" data-id="element-3904">
                    By: {event.actor}
                  </div>}
              </div>
            </div>)}
        </div>
      </div>
      {/* Evidence Form Modal */}
      {showEvidenceForm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-3905">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-3906">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowEvidenceForm(false)} data-id="element-3907">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-3908"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-3909">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full" data-id="element-3910">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-3911">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4" data-id="element-3912">
                  Submit Evidence
                </h3>
                <div className="space-y-4" data-id="element-3913">
                  <div data-id="element-3914">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3915">
                      Customer Communication
                    </label>
                    <textarea rows={3} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Describe any communication with the customer..." data-id="element-3916" />
                  </div>
                  <div data-id="element-3917">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3918">
                      Shipping/Delivery Documentation
                    </label>
                    <textarea rows={2} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Tracking numbers, delivery confirmation, etc..." data-id="element-3919" />
                  </div>
                  <div data-id="element-3920">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3921">
                      Upload Supporting Documents
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center" data-id="element-3922">
                      <UploadIcon size={32} className="mx-auto text-gray-400 mb-2" data-id="element-3923" />
                      <div className="text-sm text-gray-600" data-id="element-3924">
                        Drag and drop files here, or click to browse
                      </div>
                      <div className="text-xs text-gray-500 mt-1" data-id="element-3925">
                        PDF, JPG, PNG up to 10MB each
                      </div>
                      <input type="file" multiple className="hidden" data-id="element-3926" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-3927">
                <button type="button" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm" onClick={() => setShowEvidenceForm(false)} data-id="element-3928">
                  Submit Evidence
                </button>
                <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" onClick={() => setShowEvidenceForm(false)} data-id="element-3929">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}